import { useEffect, useRef, useState } from 'react';

import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactSignatureCanvas from 'react-signature-canvas';
import { match } from 'ts-pattern';
import { z } from 'zod';

import CameraIcon from '@/components/icons/camera';
import WarningTriangleIcon from '@/components/icons/warning-triangle';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AWAGlobalApiService } from '@/services/global-api/awa-global-api.service';
import { useUser } from '@/utils/awa';

enum SignupStep {
  NAME = 'NAME',
  COMPANY = 'COMPANY',
  PHOTO = 'PHOTO',
  SIGNATURE = 'SIGNATURE',
}

const rootFormSchema = z.object({
  step: z.nativeEnum(SignupStep),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  company: z.string().min(1),
  photo: z.string().min(1),
  signature: z.string().min(1),
});

export function AWAAuthOnboardingPage() {
  const form = useForm<z.infer<typeof rootFormSchema>>({
    resolver: zodResolver(rootFormSchema),
    defaultValues: {
      step: SignupStep.NAME,
      firstName: '',
      lastName: '',
      company: '',
      photo: '',
      signature: '',
    },
  });
  const { watch } = form;

  const currentStep = watch('step');

  return (
    <FormProvider {...form}>
      <div className="text-default-text flex h-screen flex-col gap-4 px-6 py-25">
        {match(currentStep)
          .with(SignupStep.NAME, () => <NameStep />)
          .with(SignupStep.COMPANY, () => <CompanyStep />)
          .with(SignupStep.PHOTO, () => <PhotoStep />)
          .with(SignupStep.SIGNATURE, () => <SignatureStep />)
          .exhaustive()}
      </div>
    </FormProvider>
  );
}

const nameFormSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
});

function NameStep() {
  const rootForm = useFormContext<z.infer<typeof rootFormSchema>>();

  const form = useForm<z.infer<typeof nameFormSchema>>({
    resolver: zodResolver(nameFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
    },
  });

  function handleSubmit(data: z.infer<typeof nameFormSchema>) {
    rootForm.setValue('firstName', data.firstName);
    rootForm.setValue('lastName', data.lastName);
    rootForm.setValue('step', SignupStep.COMPANY);
  }

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Whats your name?</h1>

      <p>This is required to create your digital ID. Please enter your first and last name.</p>

      <Controller
        control={form.control}
        name="firstName"
        render={({ field }) => (
          <Label className="flex flex-col gap-1 text-base">
            <span>First name</span>
            <Input
              value={field.value}
              placeholder="Enter your first name..."
              className="text-base"
              error={!!form.formState.errors.firstName}
              onChange={field.onChange}
            />
          </Label>
        )}
      />

      <Controller
        control={form.control}
        name="lastName"
        render={({ field }) => (
          <Label className="flex flex-col gap-1 text-base">
            <span>Last name</span>
            <Input
              value={field.value}
              placeholder="Enter your last name..."
              className="text-base"
              error={!!form.formState.errors.lastName}
              onChange={field.onChange}
            />
          </Label>
        )}
      />

      <Button className="text-base" disabled={!form.formState.isValid}>
        Next
      </Button>
    </form>
  );
}

const companyFormSchema = z.object({
  company: z.string().min(1),
});

function CompanyStep() {
  const rootForm = useFormContext<z.infer<typeof rootFormSchema>>();
  const { watch: watchRootForm } = rootForm;

  const currentStep = watchRootForm('step');

  const form = useForm<z.infer<typeof companyFormSchema>>({
    resolver: zodResolver(companyFormSchema),
    defaultValues: {
      company: '',
    },
  });

  function handleSubmit(data: z.infer<typeof companyFormSchema>) {
    rootForm.setValue('company', data.company);
    rootForm.setValue('step', SignupStep.PHOTO);
  }

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">What company do you work for?</h1>

      <p>This is required to be displayed on your digital ID. Please enter your current employer.</p>

      <Controller
        control={form.control}
        name="company"
        render={({ field }) => (
          <Label className="flex flex-col gap-1 text-base">
            <span>Company name</span>
            <Input
              value={field.value}
              disabled={currentStep !== SignupStep.COMPANY}
              placeholder="Enter your company name..."
              className="text-base"
              error={!!form.formState.errors.company}
              onChange={field.onChange}
            />
          </Label>
        )}
      />

      <Button className="text-base" disabled={!form.formState.isValid}>
        Next
      </Button>
    </form>
  );
}

const photoFormSchema = z.object({
  photo: z.string().min(1),
});

function PhotoStep() {
  const rootForm = useFormContext<z.infer<typeof rootFormSchema>>();

  const photoInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<z.infer<typeof photoFormSchema>>({
    resolver: zodResolver(photoFormSchema),
    defaultValues: {
      photo: '',
    },
  });

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [noCameraPermission, setNoCameraPermission] = useState(false);
  const [photoTaken, setPhotoTaken] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    async function setupCamera() {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user' },
          audio: false,
        });
        setStream(mediaStream);

        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      } catch (err) {
        setNoCameraPermission(true);
        console.error(err);
      }
    }

    if (typeof window !== 'undefined' && !photoTaken) {
      setupCamera();
    } else if (photoTaken && stream) {
      stream.getTracks().forEach((track) => track.stop());
    }

    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoTaken]);

  function handleTakePhoto() {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    const { videoWidth, videoHeight } = video;
    const size = 200;

    canvas.width = size;
    canvas.height = size;

    context.beginPath();
    context.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    context.closePath();
    context.clip();

    const scale = Math.max(size / videoWidth, size / videoHeight);
    const scaledWidth = videoWidth * scale;
    const scaledHeight = videoHeight * scale;
    const offsetX = (size - scaledWidth) / 2;
    const offsetY = (size - scaledHeight) / 2;

    context.drawImage(video, offsetX, offsetY, scaledWidth, scaledHeight);

    const photoData = canvas.toDataURL('image/jpeg');
    form.setValue('photo', photoData);
    form.trigger('photo');

    setPhotoTaken(true);
  }

  function handleRetakePhoto() {
    form.setValue('photo', '');
    setPhotoTaken(false);
  }

  const photoValue = form.watch('photo');

  function handleSubmit(data: z.infer<typeof photoFormSchema>) {
    rootForm.setValue('photo', data.photo);
    rootForm.setValue('step', SignupStep.SIGNATURE);
  }

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Add a photo</h1>

      <p>Please make sure your face is visible and not obstructed.</p>

      <div className="bg-grey-2 relative mx-auto size-[200px] overflow-hidden rounded-full">
        {photoTaken && photoValue ? (
          <img src={photoValue} alt="Your photo" className="size-full object-cover" />
        ) : (
          <>
            <CameraIcon className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />

            {!noCameraPermission && (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="absolute top-1/2 left-1/2 z-50 min-h-full min-w-full -translate-x-1/2 -translate-y-1/2 object-cover"
              />
            )}
          </>
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>

      {noCameraPermission && !photoValue && (
        <span className="text-default-orange flex items-center justify-center gap-1">
          <WarningTriangleIcon />
          Camera unauthorised, upload to continue.
        </span>
      )}

      {!noCameraPermission ? (
        photoTaken ? (
          <Button className="text-base" type="button" variant="grey" onClick={handleRetakePhoto}>
            Retake
          </Button>
        ) : (
          <Button className="text-base" type="button" variant="success" onClick={handleTakePhoto} disabled={!stream}>
            Take photo
          </Button>
        )
      ) : (
        <Controller
          control={form.control}
          name="photo"
          render={({ field: { onChange } }) => (
            <>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      const dataUrl = reader.result as string;
                      onChange(dataUrl);
                      setPhotoTaken(true);
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
              <Button className="text-base" type="button" variant="success" onClick={() => photoInputRef.current?.click()}>
                Upload photo
              </Button>
            </>
          )}
        />
      )}

      <Button className="text-base" disabled={!form.formState.isValid}>
        Next
      </Button>
    </form>
  );
}

const signatureFormSchema = z.object({
  signature: z.string().min(1),
});

function SignatureStep() {
  const queryClient = useQueryClient();
  const rootForm = useFormContext<z.infer<typeof rootFormSchema>>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const rawRedirect = searchParams.get('rawRedirect');
  const workspaceId = searchParams.get('workspaceId');
  const redirectTo = searchParams.get('redirectTo');

  const form = useForm<z.infer<typeof signatureFormSchema>>({
    resolver: zodResolver(signatureFormSchema),
    defaultValues: {
      signature: '',
    },
  });

  const { data: user } = useUser();

  const { mutateAsync: uploadData } = useMutation({
    mutationFn: (params: Parameters<typeof AWAGlobalApiService.uploadData>[0]) => AWAGlobalApiService.uploadData(params),
  });

  const canvasRef = useRef<ReactSignatureCanvas | null>(null);

  function handleClear() {
    canvasRef.current?.clear();
    form.setValue('signature', '');
  }

  async function handleSubmit(data: z.infer<typeof signatureFormSchema>) {
    const photo = rootForm.getValues('photo');

    const ops = [
      uploadData({
        data: data.signature,
        type: 'signature',
        fileName: `${user?.id}-${user?.firstName}-${user?.lastName}-${user?.companyName}-signature.png`,
      }),
    ];
    if (photo) {
      ops.push(
        uploadData({
          data: photo,
          type: 'avatar',
          fileName: `${user?.id}-${user?.firstName}-${user?.lastName}-${user?.companyName}-avatar.png`,
        }),
      );
    }

    const [signatureUrl, avatarUrl] = await Promise.all(ops);

    const updatedUser = await AWAGlobalApiService.updateUser({
      signatureUrl: signatureUrl.url,
      ...(photo && avatarUrl && { avatarUrl: avatarUrl.url }),
      firstName: rootForm.getValues('firstName'),
      lastName: rootForm.getValues('lastName'),
      companyName: rootForm.getValues('company'),
      fullName: `${rootForm.getValues('firstName')} ${rootForm.getValues('lastName')}`,
    });
    queryClient.setQueryData(['awa-user'], {
      ...updatedUser,
      id: updatedUser._id,
    });

    navigate(rawRedirect ? `/${redirectTo}` : `/${workspaceId}/${redirectTo || 'maintenance'}`);
  }

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Add your signature</h1>

      <p>This is required to be displayed on your digital ID.</p>

      <Controller
        control={form.control}
        name="signature"
        render={({ field }) => (
          <div className="rounded-md border">
            <ReactSignatureCanvas
              ref={canvasRef}
              onEnd={() => {
                const dataUrl = canvasRef.current?.toDataURL('image/png');
                if (dataUrl) {
                  field.onChange(dataUrl);
                }
              }}
              penColor="black"
              canvasProps={{
                style: { width: '100%', height: '150px' },
              }}
            />
          </div>
        )}
      />

      <Button className="text-base" type="button" variant="grey" onClick={handleClear}>
        Clear
      </Button>

      <Button className="text-base" disabled={!form.formState.isValid} loading={form.formState.isSubmitting}>
        Save
      </Button>
    </form>
  );
}
